import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Pickaxe, Loader2, Building2, Briefcase, Home } from "lucide-react";
import { PopiaDialog, POPIA_TITLE } from "../components/Popia";

export default function Register() {
  const { register, meta } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("smme");
  const [form, setForm] = useState({ name: "", surname: "", email: "", password: "", mine_name: "" });
  const [popiaAccepted, setPopiaAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (role === "buyer" && !form.mine_name) {
      setError("Please select your mine operation.");
      return;
    }
    if (!popiaAccepted) {
      setError("Please read and accept the POPIA & Data Handling Disclaimer to continue.");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, role });
      toast.success("Account created — welcome!");
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6 py-12">
      <div className="w-full max-w-lg bg-white rounded-xl border border-slate-200 shadow-sm p-7 sm:p-9">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-3">
            <img src="/assmang-logo.png" alt="Assmang logo" className="h-10 w-auto" />
            <span className="font-display font-bold text-slate-900">ASSMANG SMME PORTAL</span>
          </Link>
          <Link to="/" data-testid="register-home-button"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#00427A] border border-slate-200 hover:border-[#00427A] rounded-md px-3 py-1.5 transition-colors">
            <Home className="h-4 w-4" /> Home
          </Link>
        </div>
        <h1 className="font-display text-3xl font-black text-slate-900 tracking-tight">Create Account</h1>
        <p className="text-slate-500 mt-2 mb-6">Register for instant access to the portal.</p>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { key: "smme", label: "SMME Supplier", icon: Building2, desc: "Register your company" },
            { key: "buyer", label: "Mine Buyer", icon: Briefcase, desc: "Post opportunities" },
          ].map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`role-${r.key}-button`}
              onClick={() => setRole(r.key)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                role === r.key ? "border-[#00427A] bg-[#00427A]/5" : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <r.icon className={`h-5 w-5 mb-2 ${role === r.key ? "text-[#00427A]" : "text-slate-400"}`} />
              <p className="font-semibold text-sm text-slate-900">{r.label}</p>
              <p className="text-xs text-slate-500">{r.desc}</p>
            </button>
          ))}
        </div>

        {error && (
          <div data-testid="register-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label>
              <input data-testid="register-name-input" required value={form.name} onChange={set("name")}
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Surname</label>
              <input data-testid="register-surname-input" required value={form.surname} onChange={set("surname")}
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
            <input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")}
              className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
          </div>
          {role === "buyer" && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Name of Mine</label>
              <select data-testid="register-mine-select" value={form.mine_name} onChange={set("mine_name")}
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 bg-white focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none">
                <option value="">Select mine operation…</option>
                {(meta?.mines || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
            <input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")}
              className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none" />
          </div>
          <label data-testid="register-popia-consent" className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3.5 cursor-pointer">
            <input type="checkbox" data-testid="register-popia-checkbox" checked={popiaAccepted}
              onChange={(e) => setPopiaAccepted(e.target.checked)}
              className="h-4 w-4 mt-0.5 shrink-0 accent-[#00427A]" />
            <span className="text-xs text-slate-600 leading-relaxed">
              I have read and accept the{" "}
              <PopiaDialog trigger={
                <button type="button" data-testid="register-popia-link" className="text-[#00427A] font-semibold hover:underline">{POPIA_TITLE}</button>
              } />
              , and consent to the processing of my personal information as described.
            </span>
          </label>
          <button data-testid="register-submit-button" disabled={loading || !popiaAccepted}
            className="w-full bg-[#F2B50C] hover:bg-[#D99E00] text-[#0A2540] font-semibold py-2.5 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create Account
          </button>
        </form>
        <p className="text-sm text-slate-500 mt-6 text-center">
          Already registered?{" "}
          <Link to="/login" data-testid="go-login-link" className="text-[#00427A] font-semibold hover:underline">Sign In</Link>
        </p>
      </div>
    </div>
  );
}
